import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Check,
  Download,
  Plus
} from 'lucide-react';
import { useResearchStore } from '../store/researchStore';
import { toast } from 'react-hot-toast';
import { useProjectData, copyAgentApi } from '../api/copyAgentApi';
import { useDashboardData } from '../api/researchApi';
import { API_BASE_URL } from '../config/api';

// Types
interface ChatMessage {
  id: string;
  type: 'user' | 'agent';
  content: string;
  timestamp: Date;
  step: StepType;
  improvedVersion?: string; // Add improved version field
}

interface CopyStep {
  id: string;
  name: string;
  description: string;
  status: 'pending' | 'active' | 'completed';
  messages: ChatMessage[];
}

interface CopyModule {
  id: string;
  name: string;
  status: 'pending' | 'active' | 'completed';
  content?: string;
  options?: Array<{
    id: string;
    title: string;
    content: string;
  }>;
}

interface MainStep {
  id: string;
  name: string;
  status: 'pending' | 'active' | 'completed';
  subSteps?: CopyStep[];
  modules?: CopyModule[];
}

type StepType = 'tone-emotion' | 'brand-calibration' | 'analyze-extract' | 'framework' | 'emotional-mapping';

const CopyAgentPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { currentProject } = useResearchStore();
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Helper function to get project image (same logic as Research Landing page)
  const getProjectImage = (project: any) => {
    const isAbsolute = (u: string) => /^https?:\/\//i.test(u);
    const buildUrl = (u: string) => (isAbsolute(u) ? u : `${API_BASE_URL}${u}`);

    // Prefer explicit URL provided by backend
    if (project.primary_image_url) {
      return buildUrl(project.primary_image_url);
    }
    // Fallbacks to structured image object if present
    if (project.primary_image?.image_url) {
      return buildUrl(project.primary_image.image_url);
    }
    if (project.primary_image?.original_url) {
      return buildUrl(project.primary_image.original_url);
    }
    // No image available — show neutral placeholder
    return 'data:image/svg+xml;utf8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="160" height="160"><rect width="160" height="160" fill="#F0F0F0"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="12" fill="#9CA3AF">No Image</text></svg>');
  };
  
  // Check for autoStart parameter
  const searchParams = new URLSearchParams(window.location.search);
  const shouldAutoStart = searchParams.get('autoStart') === 'true';
  
  const isDemoMode = projectId === 'demo';
  
  // Backend API hooks
  const { data: backendProjectData } = useProjectData(
    isDemoMode ? null : (projectId ? parseInt(projectId) : null)
  );
  const { data: dashboardData } = useDashboardData(
    isDemoMode ? null : (projectId ? parseInt(projectId) : null)
  );
  
  const [copyAgentSession, setCopyAgentSession] = useState<any>(null);
  const [isCopyAgentStarted, setIsCopyAgentStarted] = useState(false);
  const [isStartingSession, setIsStartingSession] = useState(false);
  
  // Use only backend data - no mock content
  const projectData = backendProjectData?.project || currentProject;
  
  const [currentMainStep, setCurrentMainStep] = useState(0);
  const [currentSubStepIndex, setCurrentSubStepIndex] = useState(0);
  const [inputMessage, setInputMessage] = useState('');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [editableContent, setEditableContent] = useState<{[key: string]: string}>({});
  const [feedback, setFeedback] = useState('');
  const [isEditing, setIsEditing] = useState<{[key: string]: boolean}>({});
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regeneratedContent, setRegeneratedContent] = useState<{[key: string]: string}>({});
  const [showPreviewPopup, setShowPreviewPopup] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [chatMessages, setChatMessages] = useState<Array<{type: 'user' | 'agent', content: string}>>([]);
  const [selectedFrameworkIndex] = useState<number | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);

  // Render helper: formats simple markdown-like content with headings, bold and bullet lists
  const renderFormattedContent = (content: string) => {
    const lines = content.split('\n');
    const elements: React.ReactNode[] = [];
    let currentList: string[] = [];

    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-800 ml-4" key={`ul-${elements.length}`}>
            {currentList.map((item, idx) => (
              <li key={idx}>{renderInlineBold(item)}</li>
            ))}
          </ul>
        );
        currentList = [];
      }
    };

    const renderInlineBold = (text: string) => {
      // Replace **bold** with <strong>
      const parts = text.split(/(\*\*[^*]+\*\*)/g);
      return (
        <>
          {parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={i}>{part.slice(2, -2)}</strong>;
            }
            return <span key={i}>{part}</span>;
          })}
        </>
      );
    };

    lines.forEach((rawLine, idx) => {
      const line = rawLine.trim();

      // Empty line → paragraph break
      if (line.length === 0) {
        flushList();
        elements.push(<div className="h-2" key={`br-${idx}`} />);
        return;
      }

      // Bullet lines starting with • or -
      if (line.startsWith('• ') || line.startsWith('- ')) {
        currentList.push(line.replace(/^•\s+|^-\s+/, ''));
        return;
      }

      // If a new non-bullet line starts, flush any existing list
      flushList();

      // Headings of the form **Heading** or **Heading:**
      const headingMatch = line.match(/^\*\*(.+?)\*\*:?/);
      if (headingMatch) {
        elements.push(
          <div className="text-sm font-semibold text-purple-800 mt-2" key={`h-${idx}`}>
            {headingMatch[1]}
          </div>
        );
        return;
      }

      // Lines that end with : are section labels – render as subheading
      if (line.endsWith(':')) {
        elements.push(
          <div className="text-sm font-semibold text-purple-800 mt-2" key={`sh-${idx}`}>
            {line.slice(0, -1)}
          </div>
        );
        return;
      }

      // Regular paragraph
      elements.push(
        <p className="text-sm text-gray-900 leading-relaxed" key={`p-${idx}`}>{renderInlineBold(rawLine)}</p>
      );
    });

    // Flush list if the message ended with bullets
    flushList();

    return elements;
  };

  // Initialize copy agent session when "Generate Copy" is clicked
  const handleStartCopyAgent = async () => {
    // Only work with real backend data - no demo mode
    if (!projectId || projectId === 'demo') {
      toast.error('Please select a valid project to start the copy agent');
      return;
    }
    
    try {
      setIsStartingSession(true);
      
      // Start copy agent session with backend
      const session = await copyAgentApi.startSession(parseInt(projectId));
      setCopyAgentSession(session);
      setIsCopyAgentStarted(true);
      
      // Initialize chat with a simple welcome message instead of backend content
      setChatMessages([
        {
          type: 'agent',
          content: "Hello! I'm your copy agent. I've analyzed your product and research data. I'll start by generating the tone and emotion analysis for your copy. Once it's ready, you can review it and let me know what you'd like to improve."
        }
      ]);
      
      toast.success('Copy agent session started successfully!');
    } catch (error) {
      toast.error('Failed to start copy agent session');
    } finally {
      setIsStartingSession(false);
    }
  };

  // Initialize copy agent session when component mounts with projectId
  useEffect(() => {
    const startSession = async () => {
      if (projectId && projectId !== 'demo' && !isCopyAgentStarted && !isStartingSession && shouldAutoStart) {
        await handleStartCopyAgent();
      }
    };
    
    startSession();
  }, [projectId, isCopyAgentStarted, isStartingSession, shouldAutoStart]);


  const [mainSteps, setMainSteps] = useState<MainStep[]>([
    {
      id: 'step1',
      name: 'Tone & Emotion Check',
      status: 'active',
      subSteps: [
        {
          id: 'tone-emotion',
          name: 'Tone & Emotion Check',
          description: 'Analyze and refine the emotional tone for your copy',
          status: 'active',
          messages: []
        },
        {
          id: 'brand-calibration',
          name: 'Brand Calibration',
          description: 'Align copy with your brand voice and values',
          status: 'pending',
          messages: []
        },
        {
          id: 'analyze-extract',
          name: 'Analyze & Extract',
          description: 'Extract key insights and selling points from research',
          status: 'pending',
          messages: []
        },
        {
          id: 'framework',
          name: 'Framework',
          description: 'Structure your copy using proven frameworks',
          status: 'pending',
          messages: []
        },
        {
          id: 'emotional-mapping',
          name: 'Emotional Mapping',
          description: 'Create emotional journey for your customers',
          status: 'pending',
          messages: []
        }
      ]
    },
    {
      id: 'step2',
      name: 'Copywriting',
      status: 'pending',
      modules: []
    },
    {
      id: 'step3',
      name: 'Final Preview',
      status: 'pending'
    }
  ]);

  const currentMainStepData = mainSteps[currentMainStep];
  const currentSubStep = currentMainStepData.subSteps?.[currentSubStepIndex];

  // Auto-execute step when Tone & Emotion Check becomes active
  useEffect(() => {
    const executeStepAutomatically = async () => {
      if (
        isCopyAgentStarted && 
        copyAgentSession?.session_id && 
        currentSubStep?.id === 'tone-emotion' && 
        !copyAgentSession.step_1_result
      ) {
        try {
          const result = await copyAgentApi.executeStep(
            copyAgentSession.session_id,
            1, // Step 1 for Tone & Emotion Check
            "Please analyze the tone and emotion mapping for this product."
          );
          
          if (result.success) {
            // Update the session data with the step result
            setCopyAgentSession((prev: any) => prev ? {
              ...prev,
              step_1_result: result.step_result
            } : null);
            
            // Don't add additional chat messages - let the initial backend content be the only message
            // The step_0_result.recommended_approach is already displayed in chat
            // The step_1_result content is displayed in the main analysis section
          }
        } catch (error) {
        }
      }
    };

    executeStepAutomatically();
  }, [isCopyAgentStarted, copyAgentSession?.session_id, currentSubStep?.id, copyAgentSession?.step_1_result]);

  // Auto-execute Step 2 (Brand Calibration) when the sub-step is active
  useEffect(() => {
    const executeStepAutomatically = async () => {
      
      if (
        isCopyAgentStarted && 
        copyAgentSession?.session_id && 
        currentSubStep?.id === 'brand-calibration' && 
        !copyAgentSession.step_2_result &&
        copyAgentSession.step_1_result // Ensure Step 1 is completed first
      ) {
        try {
          
          // Create context from Step 1 tone analysis
          const step1Context = `Based on the tone and emotion analysis from Step 1:
Brand Tone: ${copyAgentSession.step_1_result.brand_vs_buyer_tone_map?.brand_tone || 'Not specified'}
Buyer Tone: ${copyAgentSession.step_1_result.brand_vs_buyer_tone_map?.buyer_tone || 'Not specified'}
Alignment Score: ${copyAgentSession.step_1_result.brand_vs_buyer_tone_map?.alignment_score || 'Not specified'}
Emotion Clusters: ${JSON.stringify(copyAgentSession.step_1_result.emotion_clusters || {})}
Disqualifier Analysis: ${JSON.stringify(copyAgentSession.step_1_result.disqualifier_map || {})}

Please generate brand calibration analysis that builds upon this tone foundation and ensures visual elements align with the established brand voice and emotional direction.`;
          
          const result = await copyAgentApi.executeStep(
            copyAgentSession.session_id,
            2, // Step 2 for Brand Calibration
            step1Context
          );
          
          
          if (result.success) {
            // Update the session data with the step result
            setCopyAgentSession((prev: any) => prev ? {
              ...prev,
              step_2_result: result.step_result
            } : null);
          }
        } catch (error) {
        }
      } else if (
        isCopyAgentStarted && 
        copyAgentSession?.session_id && 
        currentSubStep?.id === 'brand-calibration' && 
        !copyAgentSession.step_1_result
      ) {
      }
    };

    executeStepAutomatically();
  }, [isCopyAgentStarted, copyAgentSession?.session_id, currentSubStep?.id, copyAgentSession?.step_2_result, copyAgentSession?.step_1_result]);

  // Auto-execute Step 3 (Analyze & Extract) once Step 2 result exists while still on Brand Calibration
  useEffect(() => {
    const runStep3 = async () => {
      try {
        if (
          isCopyAgentStarted &&
          copyAgentSession?.session_id &&
          currentSubStep?.id === 'brand-calibration' &&
          copyAgentSession?.step_2_result &&
          !(copyAgentSession as any)?.step_3_result
        ) {
          const result = await copyAgentApi.executeStep(
            copyAgentSession.session_id,
            3,
            'Auto: Analyze & Extract based on approved brand calibration.'
          );
          if (result?.success && result.step_result) {
            setCopyAgentSession((prev: any) => prev ? { ...prev, step_3_result: result.step_result } : null);
          }
        }
      } catch (e) {
      }
    };
    runStep3();
  }, [isCopyAgentStarted, copyAgentSession?.session_id, currentSubStep?.id, copyAgentSession?.step_2_result]);

  // If user navigates to Analyze & Extract and Step 3 hasn't run yet, run it and show content
  useEffect(() => {
    const runIfNeeded = async () => {
      try {
        if (
          isCopyAgentStarted &&
          copyAgentSession?.session_id &&
          currentSubStep?.id === 'analyze-extract' &&
          !(copyAgentSession as any)?.step_3_result
        ) {
          const result = await copyAgentApi.executeStep(
            copyAgentSession.session_id,
            3,
            'Auto: Analyze & Extract (user navigated to this step).'
          );
          if (result?.success && result.step_result) {
            setCopyAgentSession((prev: any) => prev ? { ...prev, step_3_result: result.step_result } : null);
            const first = { type: 'agent' as const, content: formatStep3ResultForChat(result.step_result) };
            const follow = { type: 'agent' as const, content: 'I have analyzed your research and extracted key insights above. What would you like me to refine?' };
            setChatMessages([first, follow]);
          }
        }
      } catch (e) {
      }
    };
    runIfNeeded();
  }, [isCopyAgentStarted, copyAgentSession?.session_id, currentSubStep?.id]);

  // Auto-execute Step 4 when Framework becomes active and Step 4 result is missing
  useEffect(() => {
    const runIfNeeded = async () => {
      try {
        if (
          isCopyAgentStarted &&
          copyAgentSession?.session_id &&
          currentSubStep?.id === 'framework' &&
          !(copyAgentSession as any)?.step_4_result
        ) {
          const result = await copyAgentApi.executeStep(
            copyAgentSession.session_id,
            4,
            'Auto: Framework selection after Analyze & Extract.'
          );
          if (result?.success && result.step_result) {
            setCopyAgentSession((prev: any) => prev ? { ...prev, step_4_result: result.step_result } : null);
            const first = { type: 'agent' as const, content: formatStep4ResultForChat(result.step_result) };
            const follow = { type: 'agent' as const, content: 'Select an option or ask me to refine the frameworks.' };
            setChatMessages([first, follow]);
          }
        }
      } catch (e) {
      }
    };
    runIfNeeded();
  }, [isCopyAgentStarted, copyAgentSession?.session_id, currentSubStep?.id, (copyAgentSession as any)?.step_4_result]);

  // Auto-execute Step 5 when Emotional Mapping becomes active and Step 5 result is missing
  useEffect(() => {
    const runIfNeeded = async () => {
      try {
        if (
          isCopyAgentStarted &&
          copyAgentSession?.session_id &&
          currentSubStep?.id === 'emotional-mapping' &&
          !(copyAgentSession as any)?.step_5_result
        ) {
          const result = await copyAgentApi.executeStep(
            copyAgentSession.session_id,
            5,
            'Auto: Visual + Emotional mapping after Framework selection.'
          );
          if (result?.success && result.step_result) {
            setCopyAgentSession((prev: any) => prev ? { ...prev, step_5_result: result.step_result } : null);
            const first = { type: 'agent' as const, content: formatStep5ResultForChat(result.step_result) };
            const follow = { type: 'agent' as const, content: 'Here is the visual + emotional mapping. Would you like any adjustments?' };
            setChatMessages([first, follow]);
          }
        }
      } catch (e) {
      }
    };
    runIfNeeded();
  }, [isCopyAgentStarted, copyAgentSession?.session_id, currentSubStep?.id, (copyAgentSession as any)?.step_5_result]);

  // When Framework becomes active and Step 4 results are ready, show them in chat
  useEffect(() => {
    if (
      currentMainStep === 0 &&
      currentSubStep?.id === 'framework' &&
      (copyAgentSession as any)?.step_4_result
    ) {
      const first = { type: 'agent' as const, content: formatStep4ResultForChat((copyAgentSession as any).step_4_result) };
      const follow = { type: 'agent' as const, content: 'Here are the framework options. What would you like to proceed with?' };
      setChatMessages([first, follow]);
    }
  }, [currentMainStep, currentSubStep?.id, (copyAgentSession as any)?.step_4_result]);

  // When Emotional Mapping becomes active and Step 5 results are ready, show them in chat
  useEffect(() => {
    if (
      currentMainStep === 0 &&
      currentSubStep?.id === 'emotional-mapping' &&
      (copyAgentSession as any)?.step_5_result
    ) {
      const first = { type: 'agent' as const, content: formatStep5ResultForChat((copyAgentSession as any).step_5_result) };
      const follow = { type: 'agent' as const, content: 'Here is the visual + emotional mapping. What should we refine?' };
      setChatMessages([first, follow]);
    }
  }, [currentMainStep, currentSubStep?.id, (copyAgentSession as any)?.step_5_result]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Build Copywriting modules from Step 6 result
  const buildModulesFromStep6 = (data: any): CopyModule[] => {
    if (!data || typeof data !== 'object') return [];
    const entries = Object.entries(data as Record<string, any>)
      .filter(([k]) => /^module_\d+$/i.test(String(k)) && typeof (data as any)[k] === 'object')
      .sort((a, b) => {
        const an = parseInt(String(a[0]).replace(/[^0-9]/g, '') || '0', 10);
        const bn = parseInt(String(b[0]).replace(/[^0-9]/g, '') || '0', 10);
        return an - bn;
      });
    const modules: CopyModule[] = entries.map(([modKey, modValue], idx) => {
      const num = parseInt(String(modKey).replace(/[^0-9]/g, '') || '0', 10);
      const mv: any = modValue || {};

      // Prefer explicit variants arrays if provided by backend
      let options: { id: string; title: string; content: string }[] = [];
      if (Array.isArray(mv.variants)) {
        options = (mv.variants as any[])
          .filter(v => typeof v === 'object' && (v.title || v.tone || v.name || v.label || v.content))
          .map((v, i) => ({
            id: `${modKey}_var_${i + 1}`,
            title: String(v.title || v.tone || v.name || v.label || `Variant ${i + 1}`).toUpperCase(),
            content: String(v.content || v.text || '')
          }));
      }

      // Otherwise, collect string fields as tone variants, excluding non-copy fields
      if (!options.length) {
        const EXCLUDE_KEYS = new Set([
          'seo_optimized', 'compliance_flags', 'layout_preview', 'approval_log',
          'module_emotion', 'color', 'color_psychology', 'thumbnail', 'tone_names', 'recommendation'
        ]);
        const candidateEntries = Object.entries(mv)
          .filter(([k, v]) => !EXCLUDE_KEYS.has(String(k)) && typeof v === 'string' && v.trim().length > 0);

        // Maintain preferred ordering if typical keys exist
        const order = ['approved_tone', 'primary_tone', 'bold_tone', 'warm_tone', 'clinical_tone'];
        candidateEntries.sort((a, b) => {
          const ai = order.indexOf(a[0]);
          const bi = order.indexOf(b[0]);
          if (ai !== -1 && bi !== -1) return ai - bi;
          if (ai !== -1) return -1;
          if (bi !== -1) return 1;
          return a[0].localeCompare(b[0]);
        });

        options = candidateEntries.slice(0, 3).map(([k, v]) => ({
          id: `${modKey}_${k}`,
          title: String(k).replace(/_/g, ' ').toUpperCase(),
          content: String(v)
        }));
      }

      const moduleObj: CopyModule = {
        id: `module_${num}`,
        name: `Module ${num}`,
        status: idx === 0 ? 'active' : 'pending',
        options
      };
      return moduleObj;
    });
    return modules;
  };

  // Shallow compare essential module fields to avoid unnecessary resets
  const modulesEqual = (a?: CopyModule[], b?: CopyModule[]) => {
    try {
      const pick = (mods?: CopyModule[]) => (mods || []).map(m => ({
        id: m.id,
        name: m.name,
        status: m.status,
        options: (m.options || []).map(o => ({ id: o.id, title: o.title, content: o.content }))
      }));
      return JSON.stringify(pick(a)) === JSON.stringify(pick(b));
    } catch {
      return false;
    }
  };

  // Auto-generate Step 6 (Copywriting modules) when Copywriting main step becomes active
  useEffect(() => {
    const runIfNeeded = async () => {
      try {
        if (
          currentMainStep === 1 &&
          isCopyAgentStarted &&
          copyAgentSession?.session_id &&
          !(copyAgentSession as any)?.step_6_result
        ) {
          const result = await copyAgentApi.executeStep(
            copyAgentSession.session_id,
            6,
            'Generate copy modules with three tones (bold, warm, clinical) based on onboarding form, research, and approved steps 1-5.'
          );
          if (result?.success && result.step_result) {
            setCopyAgentSession((prev: any) => prev ? { ...prev, step_6_result: result.step_result } : null);
            const modules = buildModulesFromStep6(result.step_result);
            if (modules.length) {
              setMainSteps(prev => prev.map((step, idx) => 
                idx === 1
                  ? { ...step, modules: modules }
                  : step
              ));
              setCurrentModuleIndex(0);
            }
          }
        }
      } catch (e) {
      }
    };
    runIfNeeded();
  }, [currentMainStep, isCopyAgentStarted, copyAgentSession?.session_id, (copyAgentSession as any)?.step_6_result]);

  // If Step 6 result exists, (re)hydrate modules so options reflect backend (replace any mock)
  useEffect(() => {
    if (currentMainStep !== 1) return;
    const step6 = (copyAgentSession as any)?.step_6_result;
    if (!step6) return;
    const built = buildModulesFromStep6(step6);
    if (!built.length) return;
    const existing = mainSteps[1]?.modules || [];
    if (!modulesEqual(existing, built)) {
      setMainSteps(prev => prev.map((step, idx) => 
        idx === 1
          ? { ...step, modules: built }
          : step
      ));
      setCurrentModuleIndex(0);
    }
  }, [currentMainStep, mainSteps[1]?.modules, (copyAgentSession as any)?.step_6_result]);

  // Update chat messages when sub-step changes
  useEffect(() => {
    if (currentMainStep === 0 && currentSubStep) {
      // For Step 1 (Tone & Emotion Check), show backend content in chat
      if (currentSubStep.id === 'tone-emotion' && copyAgentSession?.step_1_result && !copyAgentSession?.step_1_regenerated) {
        const firstMessage = {
          type: 'agent' as const,
          content: formatStep1ResultForChat(copyAgentSession.step_1_result)
        };
        const followUp = {
          type: 'agent' as const,
          content: "I've generated the tone & emotion analysis above. What would you like me to adjust or improve?"
        };
        setChatMessages([firstMessage, followUp]);
      }
      // For Step 2 (Brand Calibration), use backend content if available
      else if (currentSubStep.id === 'brand-calibration' && copyAgentSession?.step_2_result) {
        // Push the backend-generated Step 2 content as the first chat message
        const firstMessage = {
          type: 'agent' as const,
          content: formatStep2ResultForChat(copyAgentSession.step_2_result)
        };
        const followUp = {
          type: 'agent' as const,
          content: "I've generated the brand calibration analysis above as a detailed message. What would you like me to adjust or improve?"
        };
        setChatMessages([firstMessage, followUp]);
      }
      // For Step 3 (Analyze & Extract)
      else if (currentSubStep.id === 'analyze-extract') {
        if ((copyAgentSession as any)?.step_3_result) {
          const first = { type: 'agent' as const, content: formatStep3ResultForChat((copyAgentSession as any).step_3_result) };
          const follow = { type: 'agent' as const, content: 'I have analyzed your research and extracted key insights above. What would you like me to refine?' };
          setChatMessages([first, follow]);
        } else {
          setChatMessages([
            { type: 'agent' as const, content: 'Preparing Analyze & Extract insights... Please wait a moment.' }
          ]);
        }
      }
      // For Step 4 (Framework)
      else if (currentSubStep.id === 'framework') {
        if ((copyAgentSession as any)?.step_4_result) {
          const first = { type: 'agent' as const, content: formatStep4ResultForChat((copyAgentSession as any).step_4_result) };
          const follow = { type: 'agent' as const, content: 'Here are the framework options. What would you like to proceed with?' };
          setChatMessages([first, follow]);
        } else {
          setChatMessages([
            { type: 'agent' as const, content: 'Preparing framework options... Please wait a moment.' }
          ]);
        }
      }
      // For Step 5 (Emotional Mapping)
      else if (currentSubStep.id === 'emotional-mapping') {
        if ((copyAgentSession as any)?.step_5_result) {
          const first = { type: 'agent' as const, content: formatStep5ResultForChat((copyAgentSession as any).step_5_result) };
          const follow = { type: 'agent' as const, content: 'Here is the visual + emotional mapping. Would you like any adjustments?' };
          setChatMessages([first, follow]);
        } else {
          setChatMessages([
            { type: 'agent' as const, content: 'Preparing visual + emotional mapping... Please wait a moment.' }
          ]);
        }
      }
      // For other steps, show empty chat until backend content is available
      else if (!copyAgentSession?.step_1_result && !copyAgentSession?.step_2_result) {
        setChatMessages([]);
      }
    }
  }, [currentSubStepIndex, currentMainStep, currentSubStep, copyAgentSession?.step_1_result, copyAgentSession?.step_1_regenerated, copyAgentSession?.step_2_result, (copyAgentSession as any)?.step_3_result]);

  // When Analyze & Extract becomes active and Step 3 results are ready, show them in chat
  useEffect(() => {
    if (
      currentMainStep === 0 &&
      currentSubStep?.id === 'analyze-extract' &&
      (copyAgentSession as any)?.step_3_result
    ) {
      const first = {
        type: 'agent' as const,
        content: formatStep3ResultForChat((copyAgentSession as any).step_3_result)
      };
      const follow = {
        type: 'agent' as const,
        content: 'I have analyzed your research and extracted key insights above. What would you like me to refine?'
      };
      setChatMessages([first, follow]);
    }
  }, [currentMainStep, currentSubStep?.id, (copyAgentSession as any)?.step_3_result]);

  // Function to detect if a message is asking for improvements
  const isImprovementPrompt = (message: string): boolean => {
    const improvementKeywords = [
      'improve', 'better', 'enhance', 'upgrade', 'refine', 'polish', 'optimize',
      'fix', 'correct', 'adjust', 'modify', 'change', 'update', 'revise',
      'make it', 'can you', 'could you', 'please', 'help me'
    ];
    
    const lowerMessage = message.toLowerCase();
    return improvementKeywords.some(keyword => lowerMessage.includes(keyword));
  };

  // Format Step 2 (Brand Calibration) result into a chat-friendly string
  const formatStep2ResultForChat = (data: any): string => {
    if (!data) return '';
    const parts: string[] = [];
    parts.push('**Brand Calibration Analysis**\n');

    if (data.tone_visual_mismatch_report) {
      parts.push('**Tone Visual Mismatch Report:**');
      if (typeof data.tone_visual_mismatch_report === 'string') {
        parts.push(String(data.tone_visual_mismatch_report));
      } else {
        parts.push(
          Object.entries(data.tone_visual_mismatch_report as Record<string, any>)
            .map(([platform, mismatches]) => {
              if (Array.isArray(mismatches)) {
                return `• ${platform.replace('_',' ')}: ${mismatches.join(', ')}`;
              }
              return `• ${platform.replace('_',' ')}: ${String(mismatches)}`;
            })
            .join('\n')
        );
      }
      parts.push('');
    }

    if (data.suggested_fixes) {
      parts.push('**Suggested Fixes:**');
      if (typeof data.suggested_fixes === 'string') {
        parts.push(String(data.suggested_fixes));
      } else {
        parts.push(
          Object.entries(data.suggested_fixes as Record<string, any>)
            .map(([category, fixes]) => {
              if (Array.isArray(fixes)) {
                return `• ${category.replace('_',' ')}: ${fixes.join(', ')}`;
              }
              return `• ${category.replace('_',' ')}: ${String(fixes)}`;
            })
            .join('\n')
        );
      }
      parts.push('');
    }

    if (data.mockup_descriptions) {
      parts.push('**Mockup Descriptions:**');
      if (Array.isArray(data.mockup_descriptions)) {
        parts.push((data.mockup_descriptions as any[]).map((d: any) => `• ${String(d)}`).join('\n'));
      } else {
        parts.push(String(data.mockup_descriptions));
      }
      parts.push('');
    }

    if (data.approval_question) {
      parts.push('**Review Question:**');
      parts.push(String(data.approval_question));
      parts.push('');
    }

    if (data.confidence_score) {
      parts.push(`**Confidence Score:** ${String(data.confidence_score)}`);
    }

    return parts.filter(Boolean).join('\n');
  };

  // Format Step 1 (Tone & Emotion) result into a chat-friendly string
  const formatStep1ResultForChat = (data: any): string => {
    if (!data) return '';
    const parts: string[] = [];
    parts.push('**Tone & Emotion Check**\n');

    if (data.brand_vs_buyer_tone_map) {
      parts.push('**Brand vs Buyer Tone Analysis:**');
      parts.push(`• Brand Tone: ${data.brand_vs_buyer_tone_map.brand_tone || 'N/A'}`);
      parts.push(`• Buyer Tone: ${data.brand_vs_buyer_tone_map.buyer_tone || 'N/A'}`);
      parts.push(`• Alignment Score: ${data.brand_vs_buyer_tone_map.alignment_score || 'N/A'}`);
      parts.push('');
    }

    if (data.emotion_clusters && typeof data.emotion_clusters === 'object') {
      parts.push('**Emotion Clusters:**');
      Object.entries(data.emotion_clusters).forEach(([emotion, phrases]) => {
        parts.push(`• ${String(emotion).charAt(0).toUpperCase() + String(emotion).slice(1)}: ${Array.isArray(phrases) ? (phrases as any[]).join(', ') : String(phrases)}`);
      });
      parts.push('');
    }

    if (data.disqualifier_map && typeof data.disqualifier_map === 'object') {
      parts.push('**Disqualifier Analysis:**');
      Object.entries(data.disqualifier_map).forEach(([category, reasons]) => {
        parts.push(`• ${String(category).replace('_',' ').charAt(0).toUpperCase() + String(category).replace('_',' ').slice(1)}: ${Array.isArray(reasons) ? (reasons as any[]).join(', ') : String(reasons)}`);
      });
      parts.push('');
    }

    if (data.recommendation) {
      parts.push('**Recommendation:**');
      parts.push(String(data.recommendation));
      parts.push('');
    }

    if (data.approval_question) {
      parts.push('**Review Question:**');
      parts.push(String(data.approval_question));
      parts.push('');
    }

    if (data.confidence_score) {
      parts.push(`**Confidence Score:** ${String(data.confidence_score)}`);
    }

    return parts.filter(Boolean).join('\n');
  };

  // Format Step 3 (Analyze & Extract) result into a chat-friendly string
  const formatStep3ResultForChat = (data: any): string => {
    if (!data) return '';
    const parts: string[] = [];
    parts.push('**Analyze & Extract**\n');

    const pushSection = (title: string, value: any) => {
      parts.push(`**${title}:**`);
      if (typeof value === 'string') {
        parts.push(String(value));
      } else if (Array.isArray(value)) {
        parts.push(value.map(v => `• ${String(v)}`).join('\n'));
      } else if (value && typeof value === 'object') {
        parts.push(
          Object.entries(value as Record<string, any>)
            .map(([k, v]) => `• ${k.replace('_',' ')}: ${Array.isArray(v) ? v.join(', ') : String(v)}`)
            .join('\n')
        );
      }
      parts.push('');
    };

    // Render canonical Step 3 fields first (these are what backend returns)
    if ('differentiator_table' in data) {
      const dt = (data as any).differentiator_table;
      parts.push('**Differentiator Table:**');
      if (dt && typeof dt === 'object') {
        const rows: string[] = [];
        const renderRow = (label: string, arr: any) => {
          if (Array.isArray(arr) && arr.length) {
            rows.push(`• ${label}: ${arr.map((x: any) => String(x)).join(', ')}`);
          }
        };
        renderRow('✅ Valid', dt.valid);
        renderRow('⚠️ Weak', dt.weak);
        renderRow('🚫 Invalid', dt.invalid);
        if (rows.length) parts.push(rows.join('\n'));
      }
      parts.push('');
    }

    if ('buyer_truths' in data) pushSection('Buyer Truths', (data as any).buyer_truths);
    if ('sticky_keywords' in data) pushSection('Sticky Keywords', (data as any).sticky_keywords);
    if ('trust_gaps' in data) pushSection('Trust Gaps', (data as any).trust_gaps);
    if ('review_quotes_by_theme' in data) pushSection('Review Quotes by Theme', (data as any).review_quotes_by_theme);

    // Try common fields; gracefully handle unknown structures
    if ('key_insights' in data) pushSection('Key Insights', (data as any).key_insights);
    if ('selling_points' in data) pushSection('Selling Points', (data as any).selling_points);
    if ('pain_points' in data) pushSection('Pain Points', (data as any).pain_points);
    if ('features_benefits' in data) pushSection('Features & Benefits', (data as any).features_benefits);
    if ('headlines' in data) pushSection('Headlines', (data as any).headlines);
    if ('cta_suggestions' in data) pushSection('CTA Suggestions', (data as any).cta_suggestions);

    if ((data as any).approval_question) {
      parts.push('**Review Question:**');
      parts.push(String((data as any).approval_question));
      parts.push('');
    }
    if ((data as any).confidence_score) {
      parts.push(`**Confidence Score:** ${String((data as any).confidence_score)}`);
    }

    // If nothing matched, dump a generic listing
    if (parts.length <= 2) {
      Object.entries(data as Record<string, any>).forEach(([k, v]) => pushSection(k.replace('_',' '), v));
    }

    return parts.filter(Boolean).join('\n');
  };

  // Format Step 4 (Framework Selection) result into a chat-friendly string
  const formatStep4ResultForChat = (data: any): string => {
    if (!data) return '';
    const parts: string[] = [];
    parts.push('**Framework Selection**\n');

    if (Array.isArray((data as any).framework_options)) {
      (data as any).framework_options.forEach((opt: any, idx: number) => {
        parts.push(`**Option ${idx + 1}: ${opt?.name || 'Framework'}**`);
        if (opt?.visual_layout) parts.push(`• Visual Layout: ${String(opt.visual_layout)}`);
        if (opt?.strategic_rationale) parts.push(`• Rationale: ${String(opt.strategic_rationale)}`);
        if (opt?.projected_metrics && typeof opt.projected_metrics === 'object') {
          const m = opt.projected_metrics;
          const metrics: string[] = [];
          if (m.conversion_rate) metrics.push(`CR: ${m.conversion_rate}`);
          if (m.click_through_rate) metrics.push(`CTR: ${m.click_through_rate}`);
          if (m.time_on_page) metrics.push(`Time on Page: ${m.time_on_page}`);
          if (metrics.length) parts.push(`• Projected Metrics: ${metrics.join(', ')}`);
        }
        if (opt?.cta_mapping) {
          parts.push(`• CTA Mapping: ${Array.isArray(opt.cta_mapping) ? opt.cta_mapping.join(', ') : String(opt.cta_mapping)}`);
        }
        if (opt?.module_count) parts.push(`• Module Count: ${String(opt.module_count)}`);
        parts.push('');
      });
    }

    if ((data as any).recommendation) {
      parts.push('**Recommendation:**');
      parts.push(String((data as any).recommendation));
      parts.push('');
    }
    if ((data as any).approval_question) {
      parts.push('**Review Question:**');
      parts.push(String((data as any).approval_question));
      parts.push('');
    }
    if ((data as any).confidence_score) {
      parts.push(`**Confidence Score:** ${String((data as any).confidence_score)}`);
    }

    if (parts.length <= 2) {
      Object.entries(data as Record<string, any>).forEach(([k, v]) => {
        parts.push(`**${k.replace('_',' ')}:**`);
        if (Array.isArray(v)) parts.push(v.map(x => `• ${String(x)}`).join('\n')); else if (v && typeof v === 'object') parts.push(Object.entries(v).map(([kk, vv]) => `• ${kk}: ${String(vv)}`).join('\n')); else parts.push(String(v));
        parts.push('');
      });
    }

    return parts.filter(Boolean).join('\n');
  };

  // Format Step 5 (Visual + Emotional Mapping) result into a chat-friendly string
  const formatStep5ResultForChat = (data: any): string => {
    if (!data) return '';
    const parts: string[] = [];
    parts.push('**Visual + Emotional Mapping**\n');

    if ((data as any).module_emotions && typeof (data as any).module_emotions === 'object') {
      parts.push('**Module Emotions:**');
      Object.entries((data as any).module_emotions).forEach(([mod, emo]) => {
        parts.push(`• ${String(mod).replace('_',' ').toUpperCase()}: ${String(emo)}`);
      });
      parts.push('');
    }

    if ((data as any).color_psychology && typeof (data as any).color_psychology === 'object') {
      parts.push('**Color Psychology:**');
      Object.entries((data as any).color_psychology).forEach(([mod, col]) => {
        parts.push(`• ${String(mod).replace('_',' ').toUpperCase()}: ${String(col)}`);
      });
      parts.push('');
    }

    if (Array.isArray((data as any).thumbnail_mockups)) {
      parts.push('**Thumbnail Mockups:**');
      (data as any).thumbnail_mockups.forEach((item: any) => {
        parts.push(`• ${String(item)}`);
      });
      parts.push('');
    }

    if ((data as any).layout_map) {
      parts.push('**Layout Map:**');
      parts.push(String((data as any).layout_map));
      parts.push('');
    }

    if ((data as any).visual_flow) {
      parts.push('**Visual Flow:**');
      parts.push(String((data as any).visual_flow));
      parts.push('');
    }

    if ((data as any).approval_question) {
      parts.push('**Review Question:**');
      parts.push(String((data as any).approval_question));
      parts.push('');
    }
    if ((data as any).confidence_score) {
      parts.push(`**Confidence Score:** ${String((data as any).confidence_score)}`);
    }

    return parts.filter(Boolean).join('\n');
  };

  // (removed unused generateImprovedVersion)

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    // Add user message
    const userMessage = { type: 'user' as const, content: inputMessage };
    setChatMessages(prev => [...prev, userMessage]);
    setInputMessage('');

    // Always use real backend when session is available
    if (isCopyAgentStarted && copyAgentSession?.session_id) {
      try {
        // Free-form chat first: lets the agent answer any question like ChatGPT
        const chat = await copyAgentApi.chat(copyAgentSession.session_id, inputMessage);
        if (chat?.success && chat.result?.assistant_message) {
          const agentMessage = { type: 'agent' as const, content: chat.result.assistant_message };
          setChatMessages(prev => [...prev, agentMessage]);
        }

        // Also trigger step-specific regeneration/improvement when relevant
        const isImprovement = isImprovementPrompt(inputMessage);
        const stepNumber = currentSubStep?.id === 'tone-emotion' ? 1 : 
                          currentSubStep?.id === 'brand-calibration' ? 2 : 
                          currentSubStepIndex + 1;

        const result = await copyAgentApi.executeStep(copyAgentSession.session_id, stepNumber, inputMessage);
        
        if (result.success) {
          // For Step 1, check if this is regeneration (we already have step_1_result)
          // If it's regeneration, show the improved content as a chat message
          const isRegeneration = stepNumber === 1 && copyAgentSession?.step_1_result;
          
          let agentResponse;
          
          if (isRegeneration || isImprovement) {
            // Show the improved content as a formatted chat message
            const improvedContent = result.step_result;
            
            if (isImprovement) {
              // For improvement prompts, post a brand-new message with the regenerated content
              if (stepNumber === 2) {
                // Step 2: Brand Calibration formatting
                const parts: string[] = [];
                parts.push(`**Enhanced Brand Calibration**\n`);
                if (improvedContent.tone_visual_mismatch_report) {
                  parts.push(`**Tone Visual Mismatch Report:**`);
                  if (typeof improvedContent.tone_visual_mismatch_report === 'string') {
                    parts.push(String(improvedContent.tone_visual_mismatch_report));
                  } else {
                    parts.push(
                      Object.entries(improvedContent.tone_visual_mismatch_report as Record<string, any>)
                        .map(([platform, mismatches]) => {
                          if (Array.isArray(mismatches)) {
                            return `• ${platform.replace('_',' ')}: ${mismatches.join(', ')}`;
                          }
                          return `• ${platform.replace('_',' ')}: ${String(mismatches)}`;
                        })
                        .join('\n')
                    );
                  }
                  parts.push('');
                }
                if (improvedContent.suggested_fixes) {
                  parts.push(`**Suggested Fixes:**`);
                  if (typeof improvedContent.suggested_fixes === 'string') {
                    parts.push(String(improvedContent.suggested_fixes));
                  } else {
                    parts.push(
                      Object.entries(improvedContent.suggested_fixes as Record<string, any>)
                        .map(([category, fixes]) => {
                          if (Array.isArray(fixes)) {
                            return `• ${category.replace('_',' ')}: ${fixes.join(', ')}`;
                          }
                          return `• ${category.replace('_',' ')}: ${String(fixes)}`;
                        })
                        .join('\n')
                    );
                  }
                  parts.push('');
                }
                if (improvedContent.mockup_descriptions) {
                  parts.push(`**Mockup Descriptions:**`);
                  if (Array.isArray(improvedContent.mockup_descriptions)) {
                    parts.push(
                      (improvedContent.mockup_descriptions as any[])
                        .map((d: any) => `• ${String(d)}`)
                        .join('\n')
                    );
                  } else {
                    parts.push(String(improvedContent.mockup_descriptions));
                  }
                  parts.push('');
                }
                if (improvedContent.approval_question) {
                  parts.push(`**Review Question:**`);
                  parts.push(String(improvedContent.approval_question));
                  parts.push('');
                }
                if (improvedContent.confidence_score) {
                  parts.push(`**Confidence Score:** ${String(improvedContent.confidence_score)}`);
                }

                agentResponse = parts.filter(Boolean).join('\n');
              } else if (stepNumber === 3) {
                // Step 3: Analyze & Extract formatting – show as a new message
                agentResponse = formatStep3ResultForChat(improvedContent);
              } else if (stepNumber === 4) {
                // Step 4: Framework – format as a new chat message, do not overwrite session result
                agentResponse = formatStep4ResultForChat(improvedContent);
              } else if (stepNumber === 5) {
                // Step 5: Emotional Mapping – format as a new chat message, do not overwrite session result
                agentResponse = formatStep5ResultForChat(improvedContent);
              } else {
                // Default formatting (e.g., Step 1)
                agentResponse = `**Enhanced Analysis:**\n\n` +
                              `**Brand vs Buyer Tone Analysis:**\n` +
                              `• Brand Tone: ${improvedContent.brand_vs_buyer_tone_map?.brand_tone || 'N/A'}\n` +
                              `• Buyer Tone: ${improvedContent.brand_vs_buyer_tone_map?.buyer_tone || 'N/A'}\n` +
                              `• Alignment Score: ${improvedContent.brand_vs_buyer_tone_map?.alignment_score || 'N/A'}\n\n` +
                              `**Emotion Clusters:**\n` +
                              `${Object.entries(improvedContent.emotion_clusters || {}).map(([emotion, phrases]) => 
                                `• ${emotion.charAt(0).toUpperCase() + emotion.slice(1)}: ${Array.isArray(phrases) ? phrases.join(', ') : phrases}`
                              ).join('\n')}\n\n` +
                              `**Disqualifier Analysis:**\n` +
                              `${Object.entries(improvedContent.disqualifier_map || {}).map(([category, reasons]) => 
                                `• ${category.replace('_', ' ').charAt(0).toUpperCase() + category.replace('_', ' ').slice(1)}: ${Array.isArray(reasons) ? reasons.join(', ') : reasons}`
                              ).join('\n')}\n\n` +
                              `**Confidence Score:** ${improvedContent.confidence_score || 'N/A'}`;
              }
            } else {
              // For regular regeneration, show the full updated content
              agentResponse = `I've updated the analysis based on your feedback:\n\n` +
                            `**Brand vs Buyer Tone Analysis:**\n` +
                            `• Brand Tone: ${improvedContent.brand_vs_buyer_tone_map?.brand_tone || 'N/A'}\n` +
                            `• Buyer Tone: ${improvedContent.brand_vs_buyer_tone_map?.buyer_tone || 'N/A'}\n` +
                            `• Alignment Score: ${improvedContent.brand_vs_buyer_tone_map?.alignment_score || 'N/A'}\n\n` +
                            `**Emotion Clusters:**\n` +
                            `${Object.entries(improvedContent.emotion_clusters || {}).map(([emotion, phrases]) => 
                              `• ${emotion.charAt(0).toUpperCase() + emotion.slice(1)}: ${Array.isArray(phrases) ? phrases.join(', ') : phrases}`
                            ).join('\n')}\n\n` +
                            `**Disqualifier Analysis:**\n` +
                            `${Object.entries(improvedContent.disqualifier_map || {}).map(([category, reasons]) => 
                              `• ${category.replace('_', ' ').charAt(0).toUpperCase() + category.replace('_', ' ').slice(1)}: ${Array.isArray(reasons) ? reasons.join(', ') : reasons}`
                            ).join('\n')}\n\n` +
                            `**Confidence Score:** ${improvedContent.confidence_score || 'N/A'}\n\n` +
                            `Is there anything else you'd like me to adjust?`;
            }
          } else {
            agentResponse = stepNumber === 1 ? 
                           "I've analyzed your product's tone and emotion mapping. You can now review the analysis above and let me know if you'd like to improve any specific aspect - such as the brand tone, emotion clusters, or disqualifier analysis. What would you like to adjust?" :
                           (result.step_result.approval_question || "Thank you for your input. I've updated the analysis based on your feedback. Is there anything else you'd like me to adjust?");
          }
          
          // Add the step-specific response message (in addition to general chat)
          const agentMessage = { type: 'agent' as const, content: agentResponse };
          setChatMessages(prev => [...prev, agentMessage]);
          
          // Update session state without clobbering main results on improvements:
          // - Step 1 improvements: store separately as step_1_regenerated
          // - Step 2 and Step 3 improvements: do NOT overwrite stored results; we already appended a new chat message
          if (result.step_result) {
            if (stepNumber === 1) {
              setCopyAgentSession((prev: any) => prev ? {
                ...prev,
                step_1_regenerated: result.step_result
              } : null);
            } else if (
              !(stepNumber === 2 && isImprovement) &&
              !(stepNumber === 3 && isImprovement) &&
              !(stepNumber === 4 && isImprovement) &&
              !(stepNumber === 5 && isImprovement)
            ) {
              setCopyAgentSession((prev: any) => prev ? {
                ...prev,
                [`step_${stepNumber}_result`]: result.step_result
              } : null);
            }
          }
        } else {
          // Show error message if backend fails
          const errorMessage = { type: 'agent' as const, content: "I'm having trouble processing your request right now. Please try again." };
          setChatMessages(prev => [...prev, errorMessage]);
        }
      } catch (error) {
        // Show error message
        const errorMessage = { type: 'agent' as const, content: "I'm experiencing technical difficulties. Please try again in a moment." };
        setChatMessages(prev => [...prev, errorMessage]);
      }
    } else {
      // If no session is available, show message to start the session
      const noSessionMessage = { type: 'agent' as const, content: "Please start the copy agent session first to begin our conversation." };
      setChatMessages(prev => [...prev, noSessionMessage]);
    }
  };







  const handleBackToResearch = () => {
    if (isDemoMode) {
      navigate('/research-demo');
    } else {
      navigate(`/research/${projectId}`);
    }
  };

  const handleDownloadCopy = () => {
    const allMessages = mainSteps.flatMap(step => 
      step.subSteps?.flatMap(subStep => subStep.messages) || []
    );
    const content = allMessages.map(msg => 
      `${msg.type.toUpperCase()}: ${msg.content}`
    ).join('\n\n');
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `copy-agent-${projectData?.name || 'product'}-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.success('Copy agent conversation downloaded successfully!');
  };

  const getCompletedSubSteps = () => {
    return currentMainStepData.subSteps?.filter(step => step.status === 'completed').length || 0;
  };

  const getMainContentHeight = () => {
    const completedSteps = getCompletedSubSteps();
    const totalSteps = currentMainStepData.subSteps?.length || 0;
    
    // If all sections are approved, calculate height to match approved section
    if (completedSteps === totalSteps && totalSteps > 0) {
      // Base height for header + padding + title + progress indicator
      const baseHeight = 140; // 24px padding top + bottom + header height + title + progress
      // Height per approved item (more accurate based on actual content)
      const itemHeight = 90; // Each approved section item height with spacing
      // Calculate total height needed
      let totalHeight = baseHeight + (completedSteps * itemHeight);
      
      // Add extra 20px for Emotional Mapping step
      if (currentSubStep?.id === 'emotional-mapping') {
        totalHeight += 20;
      }
      
      return `${totalHeight}px`;
    }
    
    // Default height when not all sections are approved
    return 'auto';
  };

  // Copy Generation Step Handlers
  const handleEditContent = (optionId: string, content: string) => {
    setIsEditing(prev => ({ ...prev, [optionId]: true }));
    setEditableContent(prev => ({ ...prev, [optionId]: content }));
  };

  const handleSaveContent = (optionId: string) => {
    setIsEditing(prev => ({ ...prev, [optionId]: false }));
    // Here you would typically save the content to your backend
  };

  const handleApproveModule = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);

    console.log('Module completion:', {
      currentMainStep,
      currentModuleIndex,
      totalModules: mainSteps[currentMainStep]?.modules?.length
    });

    // Move to next module if available
    const currentStep = mainSteps[currentMainStep];
    if (currentStep?.modules && currentModuleIndex < currentStep.modules.length - 1) {
      const nextModuleIndex = currentModuleIndex + 1;
      
      // Use a more robust state update approach
      setMainSteps(prev => {
        const updated = prev.map((step, index) => {
          if (index === currentMainStep && step.modules) {
            return {
              ...step,
              modules: step.modules.map((module, moduleIndex) => {
                if (moduleIndex === currentModuleIndex) {
                  return { ...module, status: 'completed' as const };
                } else if (moduleIndex === nextModuleIndex) {
                  return { ...module, status: 'active' as const };
                }
                return module;
              })
            };
          }
          return step;
        });
        
        return updated;
      });
      
      // Update current module index
      setCurrentModuleIndex(nextModuleIndex);
      
      // Clear any existing regenerated content for the new module
      setRegeneratedContent({});
      setFeedback('');
      setSelectedOptionId(null);
      
      // Debug: Log the state after updates
      setTimeout(() => {
        console.log('Module state after update:', {
          currentModuleIndex: nextModuleIndex,
          currentModuleStatus: 'active',
          previousModuleStatus: 'completed'
        });
      }, 100);
    } else {
      // If this is the last module, move to next main step
      if (currentMainStep < mainSteps.length - 1) {
        
        // Mark current module as completed
        setMainSteps(prev => prev.map((step, index) => 
          index === currentMainStep && step.modules
            ? {
                ...step,
                modules: step.modules.map((module, moduleIndex) => 
                  moduleIndex === currentModuleIndex 
                    ? { ...module, status: 'completed' as const }
                    : module
                )
              }
            : step
        ));
        
        setCurrentMainStep(prev => prev + 1);
        setCurrentModuleIndex(0);
        setRegeneratedContent({});
        setFeedback('');
        setSelectedOptionId(null);
      } else {
        
        // Mark current module as completed
        setMainSteps(prev => prev.map((step, index) => 
          index === currentMainStep && step.modules
            ? {
                ...step,
                modules: step.modules.map((module, moduleIndex) => 
                  moduleIndex === currentModuleIndex 
                    ? { ...module, status: 'completed' as const }
                    : module
                )
              }
            : step
        ));
      }
    }

    // Trigger backend execution of Step 3 (Analyze & Extract) when Brand Calibration sub-step is approved
    const executeStep3 = async () => {
      try {
        if (
          isCopyAgentStarted &&
          copyAgentSession?.session_id &&
          currentSubStep?.id === 'brand-calibration'
        ) {
          const result = await copyAgentApi.executeStep(
            copyAgentSession.session_id,
            3,
            'Proceed to Analyze & Extract based on approved brand calibration.'
          );
          if (result?.success && result.step_result) {
            // Post Step 3 as a chat message on Analyze & Extract when we switch to that sub-step
            setCopyAgentSession((prev: any) => prev ? { ...prev, step_3_result: result.step_result } : null);
          }
        }
      } catch (err) {
        console.error('Error executing step 3:', err);
      }
    };
    
    executeStep3();

    // Move to next sub-step (Analyze & Extract) if available
    if (currentMainStepData.subSteps && currentSubStepIndex < currentMainStepData.subSteps.length - 1) {
      setCurrentSubStepIndex(prev => prev + 1);
      setChatMessages(() => {
        // When we land on Analyze & Extract, emit the backend content as chat
        const nextStep = currentMainStepData.subSteps?.[currentSubStepIndex + 1];
        if (nextStep?.id === 'analyze-extract' && (copyAgentSession as any)?.step_3_result) {
          const first = {
            type: 'agent' as const,
            content: formatStep3ResultForChat((copyAgentSession as any).step_3_result)
          };
          const follow = {
            type: 'agent' as const,
            content: 'I have analyzed your research and extracted key insights above. What would you like me to refine?'
          };
          return [first, follow];
        }
        return [];
      });
    }

    // After Analyze & Extract approval, execute Step 4 and show on Framework step
    const executeStep4 = async () => {
      try {
        if (
          isCopyAgentStarted &&
          copyAgentSession?.session_id &&
          currentSubStep?.id === 'analyze-extract'
        ) {
          const result = await copyAgentApi.executeStep(
            copyAgentSession.session_id,
            4,
            'Auto: Generate framework options based on Analyze & Extract.'
          );
          if (result?.success && result.step_result) {
            setCopyAgentSession((prev: any) => prev ? { ...prev, step_4_result: result.step_result } : null);
            // Navigate to Framework step (index 3 in subSteps)
            setCurrentSubStepIndex(3);
            const first = { type: 'agent' as const, content: formatStep4ResultForChat(result.step_result) };
            const follow = { type: 'agent' as const, content: 'Here are two framework options. Which would you like to proceed with, or should I refine them?' };
            setChatMessages([first, follow]);
          }
        }
      } catch (e) {
        console.error('Error executing step 4:', e);
      }
    };
    
    executeStep4();

    // Reset feedback
    setFeedback('');
    
    // Show success message
    toast.success('Module approved successfully!');
    
    // Reset transition state
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const handleRegenerateContent = () => {
    if (!feedback.trim()) return;
    
    setIsRegenerating(true);
    
    // Get current module from mainSteps
    const currentModule = mainSteps[currentMainStep]?.modules?.[currentModuleIndex];
    
    // Simulate AI regeneration with a delay
    setTimeout(() => {
      // Generate new content based on feedback
      const newContent = `Based on your feedback: "${feedback}", here's the improved content: ${currentModule?.options?.[0]?.content || 'New content will appear here.'} This version addresses your concerns and provides a more compelling message for your target audience.`;
      
      // Update the actual module content in mainSteps instead of just regeneratedContent
      setMainSteps(prev => prev.map((step, stepIndex) => 
        stepIndex === currentMainStep && step.modules
          ? {
              ...step,
              modules: step.modules.map((module, moduleIdx) => 
                moduleIdx === currentModuleIndex
                  ? {
                      ...module,
                      options: module.options?.map((option: any, optionIndex: number) => ({
                        ...option,
                        content: `Regenerated Option ${optionIndex + 1}: ${newContent}`
                      }))
                    }
                  : module
              )
            }
          : step
      ));
      
      // Also update regenerated content for consistency
      if (currentModule?.options) {
        const newRegeneratedContent: {[key: string]: string} = {};
        currentModule.options.forEach((option: any, index: number) => {
          newRegeneratedContent[option.id] = `Regenerated Option ${index + 1}: ${newContent}`;
        });
        setRegeneratedContent(prev => ({ ...prev, ...newRegeneratedContent }));
      }
      
      // Clear feedback
      setFeedback('');
      setIsRegenerating(false);
      
      // Show success message
      toast.success('Content regenerated successfully!');
    }, 2000); // 2 second delay to simulate AI processing
  };

  const handlePreviewClick = (stepName: string, stepId?: string) => {
    setPreviewTitle(stepName);
    const id = stepId || currentMainStepData.subSteps?.find(s => s.name === stepName)?.id;
    let content = '';
    if (id === 'tone-emotion' && copyAgentSession?.step_1_result) {
      content = formatStep1ResultForChat(copyAgentSession.step_1_result);
    } else if (id === 'brand-calibration' && copyAgentSession?.step_2_result) {
      content = formatStep2ResultForChat(copyAgentSession.step_2_result);
    } else if (id === 'analyze-extract' && (copyAgentSession as any)?.step_3_result) {
      content = formatStep3ResultForChat((copyAgentSession as any).step_3_result);
    } else if (id === 'framework' && (copyAgentSession as any)?.step_4_result) {
      content = formatStep4ResultForChat((copyAgentSession as any).step_4_result);
    } else if (id === 'emotional-mapping' && (copyAgentSession as any)?.step_5_result) {
      content = formatStep5ResultForChat((copyAgentSession as any).step_5_result);
    } else {
      content = 'No approved content found for this step yet.';
    }
    setPreviewContent(content);
    setShowPreviewPopup(true);
  };


    const renderStep1Content = () => {
    if (!currentSubStep) return null;

    // Remove the useEffect from here - it's now at component level
    
    return (
      <div className="grid grid-cols-1 lg:grid-cols-10 gap-8">
        {/* Main Content Area */}
        <div className="lg:col-span-7 bg-white rounded-lg shadow-sm border flex flex-col" style={{ 
          borderRadius: '10px', 
          border: '1px solid #ECECEC',
          background: '#F8F9FE',
          padding: '24px',
          height: getMainContentHeight()
        }}>
          <h3 className="text-lg font-semibold text-gray-900 mb-4" style={{ fontWeight: '600' }}>
            {currentSubStep.name.toUpperCase()}
          </h3>
          
          {/* Backend Generated Content */}
          {currentSubStep.id === 'tone-emotion' && copyAgentSession?.step_1_result ? null : currentSubStep.id === 'tone-emotion' && !copyAgentSession?.step_1_result ? (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="text-sm font-medium text-yellow-900 mb-2">Loading AI Analysis...</h4>
              <p className="text-sm text-yellow-700">The AI is analyzing your product's tone and emotion mapping. This may take a few moments.</p>
            </div>
          ) : null}

          {/* Step 2 Backend Generated Content */}
          {currentSubStep.id === 'brand-calibration' && false && copyAgentSession?.step_2_result ? (
            <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <h4 className="text-sm font-medium text-purple-900 mb-2">AI-Generated Brand Calibration Analysis for {backendProjectData?.project?.name || 'Your Product'}</h4>
              <div className="space-y-4">
                {/* Tone Visual Mismatch Report */}
                {copyAgentSession.step_2_result.tone_visual_mismatch_report && (
                  <div>
                    <p className="text-sm font-medium text-purple-800 mb-2">Tone Visual Mismatch Report:</p>
                    <div className="bg-white p-3 rounded border">
                                              {typeof copyAgentSession.step_2_result.tone_visual_mismatch_report === 'string' ? (
                          <p className="text-sm text-gray-700 leading-relaxed">
                            {String(copyAgentSession.step_2_result.tone_visual_mismatch_report)}
                          </p>
                        ) : (
                          <div className="space-y-3">
                            {Object.entries(copyAgentSession.step_2_result.tone_visual_mismatch_report as Record<string, any>).map(([platform, mismatches]) => (
                              <div key={platform}>
                                <p className="text-sm font-semibold text-purple-700 capitalize">{platform.replace('_', ' ')}:</p>
                                {Array.isArray(mismatches) ? (
                                  <ul className="list-disc list-inside ml-4 text-sm text-gray-700">
                                    {mismatches.map((mismatch: any, idx: number) => (
                                      <li key={idx}>{String(mismatch)}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p className="text-sm text-gray-700 ml-4">{String(mismatches)}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                )}

                {/* Suggested Fixes */}
                {copyAgentSession.step_2_result.suggested_fixes && (
                  <div>
                    <p className="text-sm font-medium text-purple-800 mb-2">Suggested Fixes:</p>
                    <div className="bg-white p-3 rounded border">
                                              {typeof copyAgentSession.step_2_result.suggested_fixes === 'string' ? (
                          <p className="text-sm text-gray-700 leading-relaxed">
                            {String(copyAgentSession.step_2_result.suggested_fixes)}
                          </p>
                        ) : (
                          <div className="space-y-3">
                            {Object.entries(copyAgentSession.step_2_result.suggested_fixes as Record<string, any>).map(([category, fixes]) => (
                              <div key={category}>
                                <p className="text-sm font-semibold text-purple-700 capitalize">{category.replace('_', ' ')}:</p>
                                {Array.isArray(fixes) ? (
                                  <ul className="list-disc list-inside ml-4 text-sm text-gray-700">
                                    {fixes.map((fix: any, idx: number) => (
                                      <li key={idx}>{String(fix)}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p className="text-sm text-gray-700 ml-4">{String(fixes)}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                )}

                {/* Mockup Descriptions */}
                {copyAgentSession.step_2_result.mockup_descriptions && (
                  <div>
                    <p className="text-sm font-medium text-purple-800 mb-2">Mockup Descriptions:</p>
                    <div className="bg-white p-3 rounded border">
                                              {Array.isArray(copyAgentSession.step_2_result.mockup_descriptions) ? (
                          <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
                            {copyAgentSession.step_2_result.mockup_descriptions.map((description: any, idx: number) => (
                              <li key={idx}>{String(description)}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-gray-700 leading-relaxed">
                            {String(copyAgentSession.step_2_result.mockup_descriptions)}
                          </p>
                        )}
                    </div>
                  </div>
                )}

                {/* Approval Question */}
                {copyAgentSession.step_2_result.approval_question && (
                  <div>
                    <p className="text-sm font-medium text-purple-800 mb-2">Review Question:</p>
                    <div className="bg-white p-3 rounded border">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {copyAgentSession.step_2_result.approval_question}
                      </p>
                    </div>
                  </div>
                )}

                {/* Confidence Score */}
                {copyAgentSession.step_2_result.confidence_score && (
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-purple-800">Confidence Score:</p>
                    <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                      {copyAgentSession.step_2_result.confidence_score}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ) : currentSubStep.id === 'brand-calibration' && !copyAgentSession?.step_2_result ? (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="text-sm font-medium text-yellow-900 mb-2">
                {!copyAgentSession?.step_1_result ? 'Step 1 Required' : 'Loading Brand Calibration Analysis...'}
              </h4>
              <p className="text-sm text-yellow-700">
                {!copyAgentSession?.step_1_result 
                  ? 'Please complete the Tone & Emotion Check step first to enable brand calibration analysis.'
                  : 'The AI is analyzing your brand calibration based on the tone analysis from Step 1. This may take a few moments.'
                }
              </p>
            </div>
          ) : null}

          {/* Step 4 Backend Generated Content removed as requested */}

          {/* Framework selection will happen via chat, not cards */}

          {/* Chat Messages */}
          <div className={`space-y-4 overflow-y-auto pr-2 flex-1 ${currentSubStep?.id === 'emotional-mapping' ? 'h-96' : 'max-h-96'}`} style={{ 
            scrollbarWidth: 'thin', 
            scrollbarColor: '#8B5CF6 #E5E7EB',
            scrollbarGutter: 'stable'
          }}>
            {chatMessages.map((message, index) => (
              <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`${message.type === 'agent' ? 'w-full' : 'max-w-xs lg:max-w-md'} px-4 py-3 rounded-lg ${
                  message.type === 'user'
                    ? 'text-black'
                    : 'text-gray-900'
                }`} style={{
                  borderRadius: message.type === 'user' ? '10px' : '12px',
                  background: message.type === 'user' ? '#E2D5FF' : '#FFF',
                  border: message.type === 'user' ? 'none' : '1px solid #E2D5FF'
                }}>
                  {/* Show improvement indicator for user messages that are improvement prompts */}
                  {message.type === 'user' && isImprovementPrompt(message.content) && (
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                        🔧 Improvement Request
                      </div>
                      <div className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                        ✨ Processing...
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-1">{renderFormattedContent(message.content)}</div>
                  

                </div>
              </div>
            ))}
            

            
            <div ref={chatEndRef} />
          </div>
          

          {/* Improve Section or Next Button */}
          <div className="pt-4 border-t border-gray-200 mt-auto">
            {currentSubStepIndex === 4 && currentSubStep?.status === 'completed' ? (
              // Show Next button for Emotional Mapping (last sub-step) only after it's approved
              <div className="flex justify-center w-full">
                <button
                  onClick={() => {
                    if (isTransitioning) return; // Prevent multiple rapid clicks
                    setIsTransitioning(true);
                    
                    
                    // Mark current main step as completed and next as active
                    setMainSteps(prev => {
                      const updated = prev.map((step, index) => 
                        index === currentMainStep 
                          ? { ...step, status: 'completed' as const }
                          : index === currentMainStep + 1
                          ? { ...step, status: 'active' as const }
                          : step
                      );
                      return updated;
                    });
                    
                    // Move to next main step
                    setCurrentMainStep(prev => {
                      return prev + 1;
                    });
                    
                    // Ensure we start the Copywriting step fresh; modules will be generated by Step 6
                    setCurrentModuleIndex(0);
                    setCurrentSubStepIndex(0);
                    // Clear chat messages when moving to Step 2
                    setChatMessages([]);
                    setInputMessage('');
                    
                    // Reset transition state after a short delay
                    setTimeout(() => setIsTransitioning(false), 300);
                  }}
                  disabled={isTransitioning}
                  className="w-full px-8 py-3 text-white rounded-lg font-medium transition-all duration-200 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    borderRadius: '12px',
                    background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)',
                    minWidth: '150px'
                  }}
                >
                  {isTransitioning ? 'Loading...' : 'Next'}
                </button>
              </div>
            ) : (
              // Show improve section for other sub-steps
              <div className="flex items-center space-x-2">
                <div className="relative" style={{ width: '600px' }}>
                  <input
                    type="text"
                    placeholder="Improve this"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    style={{
                      height: '54px',
                      borderRadius: '12px',
                      border: '1px solid #4F3E98',
                      background: '#F8F9FE',
                      padding: '18px'
                    }}
                  />
                  <button 
                    onClick={handleSendMessage}
                    disabled={!inputMessage.trim()}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 26 26" fill="none">
                      <g clipPath="url(#clip0_1124_169)">
                        <path d="M13.0009 1.44531C10.7154 1.44531 8.48125 2.12303 6.58095 3.39277C4.68065 4.66252 3.19954 6.46725 2.32493 8.57875C1.45032 10.6902 1.22148 13.0137 1.66735 15.2552C2.11323 17.4968 3.21379 19.5558 4.82986 21.1719C6.44594 22.788 8.50494 23.8885 10.7465 24.3344C12.9881 24.7803 15.3115 24.5514 17.423 23.6768C19.5345 22.8022 21.3392 21.3211 22.609 19.4208C23.8787 17.5205 24.5564 15.2863 24.5564 13.0009C24.5564 9.93614 23.339 6.99694 21.1719 4.82986C19.0048 2.66277 16.0656 1.44531 13.0009 1.44531ZM18.7787 12.6903C18.5857 12.8791 18.3264 12.9848 18.0564 12.9848C17.7864 12.9848 17.5272 12.8791 17.3342 12.6903L14.012 9.38976V19.602C14.012 19.8701 13.9055 20.1273 13.7158 20.3169C13.5262 20.5066 13.269 20.6131 13.0009 20.6131C12.7327 20.6131 12.4755 20.5066 12.2859 20.3169C12.0963 20.1273 11.9898 19.8701 11.9898 19.602V9.49087L8.79032 12.6903C8.59877 12.8819 8.33898 12.9895 8.0681 12.9895C7.79721 12.9895 7.53742 12.8819 7.34587 12.6903C7.15433 12.4988 7.04672 12.239 7.04672 11.9681C7.04672 11.6972 7.15433 11.4374 7.34587 11.2459L13.0587 5.56198L18.7787 11.2603C18.8726 11.3542 18.947 11.4657 18.9979 11.5884C19.0487 11.711 19.0748 11.8425 19.0748 11.9753C19.0748 12.1081 19.0487 12.2396 18.9979 12.3623C18.947 12.485 18.8726 12.5964 18.7787 12.6903Z" fill="#4A3B8E"/>
                      </g>
                      <defs>
                        <clipPath id="clip0_1124_169">
                          <rect width="26" height="26" fill="white"/>
                        </clipPath>
                      </defs>
                    </svg>
                  </button>
                </div>
                <button 
                  onClick={() => {
                    // Create a file input element
                    const fileInput = document.createElement('input');
                    fileInput.type = 'file';
                    fileInput.accept = 'image/*,.pdf,.doc,.docx,.txt';
                    fileInput.onchange = (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) {
                        // Handle file upload - you can add your upload logic here
                        // For now, just add a message to chat
                        const uploadMessage = { type: 'user' as const, content: `📎 Uploaded: ${file.name}` };
                        setChatMessages(prev => [...prev, uploadMessage]);
                      }
                    };
                    fileInput.click();
                  }}
                  className="flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
                  style={{
                    height: '54px',
                    width: '54px',
                    borderRadius: '12px',
                    border: '1px solid #4A3B8E'
                  }}
                >
                  <Plus className="h-6 w-6" />
                </button>
                <button
                  onClick={() => {
                    // Mark current sub-step as completed
                    if (currentMainStepData.subSteps) {
                      setMainSteps(prev => prev.map((step, index) => 
                        index === currentMainStep && step.subSteps
                          ? {
                              ...step,
                              subSteps: step.subSteps.map((subStep, subIndex) => 
                                subIndex === currentSubStepIndex 
                                  ? { ...subStep, status: 'completed' }
                                  : subIndex === currentSubStepIndex + 1
                                  ? { ...subStep, status: 'active' }
                                  : subStep
                              )
                            }
                          : step
                      ));
                    }
                    
                    // If approving Framework, execute Step 5 and navigate to Emotional Mapping with content
                    (async () => {
                      try {
                        if (
                          isCopyAgentStarted &&
                          copyAgentSession?.session_id &&
                          currentSubStep?.id === 'framework'
                        ) {
                          if (selectedFrameworkIndex === null) {
                            toast.error('Please select a framework option first');
                            setIsTransitioning(false);
                            return;
                          }
                          const selected = (copyAgentSession as any)?.step_4_result?.framework_options?.[selectedFrameworkIndex] || null;
                          const result = await copyAgentApi.executeStep(
                            copyAgentSession.session_id,
                            5,
                            selected
                              ? `Generate visual + emotional mapping based on approved framework option: ${JSON.stringify(selected)}`
                              : 'Generate visual + emotional mapping based on approved framework.'
                          );
                          if (result?.success && result.step_result) {
                            setCopyAgentSession((prev: any) => prev ? { ...prev, step_5_result: result.step_result } : null);
                            setCurrentSubStepIndex(4);
                            const first = { type: 'agent' as const, content: formatStep5ResultForChat(result.step_result) };
                            const follow = { type: 'agent' as const, content: 'Here is the visual + emotional mapping. Would you like any adjustments?' };
                            setChatMessages([first, follow]);
                          }
                        }
                      } catch (e) {
                      }
                    })();

                    // Move to next sub-step
                    if (currentMainStepData.subSteps && currentSubStepIndex < currentMainStepData.subSteps.length - 1) {
                      setCurrentSubStepIndex(prev => prev + 1);
                      // Don't reset chat messages here - let the useEffect handle it
                      setInputMessage('');
                    }
                    
                    // Reset transition state after a short delay
                    setTimeout(() => setIsTransitioning(false), 300);
                  }}
                  disabled={isTransitioning}
                  className="flex items-center justify-center text-white rounded-lg font-medium transition-all duration-200 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    height: '54px',
                    width: '120px',
                    borderRadius: '12px',
                    background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)'
                  }}
                >
                  {isTransitioning ? 'Approving...' : 'Approve'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Approved Sections Sidebar */}
        <div className="lg:col-span-3 bg-white rounded-lg shadow-sm border" style={{ 
          borderRadius: '10px', 
          border: '1px solid #ECECEC',
          background: '#F8F9FE',
          padding: '24px' 
        }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900" style={{ fontWeight: '600' }}>APPROVED SECTIONS</h3>
            <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
              {getCompletedSubSteps()}/5
            </div>
          </div>
          
          <div className="space-y-4" style={{ 
            scrollbarWidth: 'thin', 
            scrollbarColor: '#8B5CF6 #E5E7EB',
            scrollbarGutter: 'stable'
          }}>
            {currentMainStepData.subSteps?.filter(step => step.status === 'completed').length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">No approved sections yet.</p>
                <p className="text-xs text-gray-400 mt-1">Complete steps from the left panel to see them here.</p>
              </div>
            ) : (
              currentMainStepData.subSteps?.filter(step => step.status === 'completed').map((step) => (
                <div key={step.id} className="border border-green-200 rounded-lg p-4 bg-green-50 transition-all" style={{
                  borderRadius: '10px',
                  border: '1px solid #279808',
                  background: '#EFFFEB'
                }}>
                  <div className="flex flex-col space-y-2">
                    <div className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 39 39" fill="none">
                        <path d="M33.75 19.7119L30.5891 15.9265L31.0295 10.9155L26.353 9.7989L23.9045 5.46875L19.5 7.45681L15.0955 5.46875L12.647 9.7989L7.97045 10.9019L8.41091 15.9128L5.25 19.7119L8.41091 23.4974L7.97045 28.522L12.647 29.6386L15.0955 33.9688L19.5 31.9671L23.9045 33.9551L26.353 29.625L31.0295 28.5084L30.5891 23.4974L33.75 19.7119ZM16.9091 26.5204L11.7273 21.0736L13.5539 19.1537L16.9091 22.6668L25.4461 13.6933L27.2727 15.6269L16.9091 26.5204Z" fill="#279808"/>
                      </svg>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-green-700">
                        {step.name}
                      </span>
                      <button 
                        className="px-3 py-1 bg-green-500 text-white text-xs rounded-full hover:bg-green-600 transition-colors"
                        onClick={() => handlePreviewClick(step.name, step.id)}
                      >
                        Preview
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStep2Content = () => {
    try {
      
      const copywritingStep = mainSteps.find(step => step.id === 'step2');
      const modules = copywritingStep?.modules || [];

      // If no modules yet, still show the Copywriting workspace mock UI
      if (!copywritingStep || !modules.length) {
        return (
          <div className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-10 gap-8">
              <div className="lg:col-span-7 bg-white rounded-lg shadow-sm border" style={{ 
                borderRadius: '10px', 
                border: '1px solid #ECECEC',
                background: '#F8F9FE',
                padding: '24px' 
              }}>
                <h3 className="text-xl font-semibold text-gray-900 mb-6">COPY GENERATION</h3>
                <div className="rounded-lg p-6" style={{
                  borderRadius: '20px',
                  border: '1px solid #E2D5FF',
                  background: '#F3F1FF'
                }}>
                  <div className="mb-3">
                    <span className="text-lg font-medium text-gray-900">Your copywriting workspace is ready</span>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed mb-4">
                    Click Approve on prior steps or use the sidebar to begin generating modules. As you approve modules, they will appear in the Approved Modules panel.
                  </p>
                </div>
              </div>
              <div className="lg:col-span-3 bg-white rounded-lg shadow-sm border" style={{ 
                borderRadius: '10px', 
                border: '1px solid #ECECEC',
                background: '#F8F9FE',
                padding: '24px' 
              }}>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">APPROVED MODULES</h3>
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">No approved modules yet.</p>
                  <p className="text-xs text-gray-400 mt-1">Complete modules to see them here.</p>
                </div>
              </div>
            </div>
          </div>
        );
      }

      const currentModule = modules[currentModuleIndex];
      
      console.log('Current module debug:', {
        currentModuleIndex,
        totalModules: modules.length,
        currentModule: currentModule?.name,
        currentModuleStatus: currentModule?.status,
        allModules: modules.map(m => ({ name: m.name, status: m.status }))
      });

      return (
        <div className="space-y-8">
          {/* Main Copywriting Area */}
          <div className="grid grid-cols-1 lg:grid-cols-10 gap-8">
            <div className="lg:col-span-7 bg-white rounded-lg shadow-sm border" style={{ 
              borderRadius: '10px', 
              border: '1px solid #ECECEC',
              background: '#F8F9FE',
              padding: '24px' 
            }}>
              <h3 className="text-xl font-semibold text-gray-900 mb-6">COPY GENERATION</h3>
              
              {/* Render Completed Modules First with Enhanced Green Approval Boxes */}
              {modules.filter(module => module.status === 'completed').map((module, index) => (
                <div key={module.id} className="mb-6">
                  <div className="relative">
                    {/* Connection Line */}
                    {index < modules.filter(m => m.status === 'completed').length - 1 && (
                      <div className="absolute left-6 top-12 w-0.5 h-8 bg-green-300"></div>
                    )}
                    
                    {/* Green Approval Box */}
                    <div className="flex items-center justify-between p-4 rounded-lg shadow-sm" style={{
                      borderRadius: '16px',
                      border: '2px solid #10B981',
                      background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.15)'
                    }}>
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full" style={{
                          background: '#10B981'
                        }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-green-800">{module.name}</div>
                          <div className="text-xs text-green-600">Module {modules.indexOf(module) + 1} - Approved</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center space-x-2 px-3 py-1 rounded-full" style={{
                          background: '#10B981'
                        }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M5 13L9 17L19 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span className="text-xs font-medium text-white">Approved</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Visual Separator between completed and current modules */}
              {modules.filter(module => module.status === 'completed').length > 0 && currentModule && (
                <div className="flex items-center my-6">
                  <div className="flex-1 h-px bg-gradient-to-r from-green-300 to-purple-300"></div>
                  <div className="px-4 py-2 bg-white rounded-full border-2 border-purple-200">
                    <span className="text-xs font-medium text-purple-700">Current Module</span>
                  </div>
                  <div className="flex-1 h-px bg-gradient-to-r from-purple-300 to-green-300"></div>
                </div>
              )}

              {/* Render Current Active Module */}
              {currentModule && (
                <div key={`module-${currentModuleIndex}-${currentModule.status}`} className="mb-6">
                  <div className="rounded-lg p-6 shadow-lg" style={{
                    borderRadius: '20px',
                    border: '2px solid #8B5CF6',
                    background: 'linear-gradient(135deg, #F3F1FF 0%, #EDE9FE 100%)',
                    boxShadow: '0 8px 25px rgba(139, 92, 246, 0.15)'
                  }}>
                    {/* Module Heading */}
                    <div className="mb-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-500 text-white text-sm font-bold">
                            {currentModuleIndex + 1}
                          </div>
                          <div>
                            <span className="text-lg font-medium text-gray-900">{currentModule.name}</span>
                            <div className="text-xs text-purple-600">Module {currentModuleIndex + 1} of {modules.length}</div>
                          </div>
                          <span className="px-3 py-1 text-xs bg-purple-500 text-white rounded-full font-medium">
                            {currentModule.status === 'active' ? 'ACTIVE' : currentModule.status === 'completed' ? 'COMPLETED' : 'PENDING'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Options or Regenerated Content */}
                    {currentModule.options && (
                      <div className="mb-6">
                        {!selectedOptionId ? (
                          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-center space-x-2">
                              <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className="text-sm text-blue-700 font-medium">Select your preferred option below to proceed with approval</span>
                            </div>
                          </div>
                        ) : (
                          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center space-x-2">
                              <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className="text-sm text-green-700 font-medium">Option selected! You can now approve this module.</span>
                            </div>
                          </div>
                        )}
                        {Object.keys(regeneratedContent).length > 0 ? (
                          /* Show Single Regenerated Content Box */
                          <div className="bg-white border border-gray-200 rounded-lg p-4" style={{
                            borderRadius: '12px',
                            border: '2px solid #564DB8',
                            background: '#FFF',
                            boxShadow: '0 2px 6px 0 rgba(86, 77, 184, 0.15)'
                          }}>
                            <div className="text-sm font-medium text-gray-900 mb-3">Regenerated Content</div>
                            <p className="text-sm text-gray-700 leading-relaxed mb-4">
                              {regeneratedContent[Object.keys(regeneratedContent)[0]] || ''}
                            </p>
                            <div className="flex justify-end">
                              <button 
                                onClick={() => setRegeneratedContent({})}
                                className="px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition-colors"
                              >
                                Show Options
                              </button>
                            </div>
                          </div>
                        ) : (
                          /* Show Multiple Options */
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {currentModule.options.map((option) => (
                              <div 
                                key={option.id} 
                                className={`bg-white border rounded-lg p-4 relative cursor-pointer transition-all ${
                                  selectedOptionId === option.id 
                                    ? 'border-purple-500 ring-2 ring-purple-200' 
                                    : 'border-gray-200 hover:border-purple-300'
                                }`}
                                onClick={() => setSelectedOptionId(option.id)}
                              >
                                <div className="flex items-center justify-between mb-3">
                                  <div className="text-sm font-medium text-gray-900">{option.title}</div>
                                  {selectedOptionId === option.id && (
                                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-purple-500">
                                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                                
                                {isEditing[option.id] ? (
                                  /* Editable Content */
                                  <div className="mb-4">
                                    <textarea
                                      value={editableContent[option.id] || option.content}
                                      onChange={(e) => setEditableContent(prev => ({ 
                                        ...prev, 
                                        [option.id]: e.target.value 
                                      }))}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                      rows={6}
                                    />
                                    <div className="flex justify-end mt-2 space-x-2">
                                      <button
                                        onClick={() => handleSaveContent(option.id)}
                                        className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors"
                                      >
                                        Save
                                      </button>
                                      <button
                                        onClick={() => setIsEditing(prev => ({ ...prev, [option.id]: false }))}
                                        className="px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition-colors"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  /* Read-only Content */
                                  <p className="text-sm text-gray-700 leading-relaxed mb-4">
                                    {editableContent[option.id] || option.content}
                                  </p>
                                )}
                                
                                {/* Edit Button */}
                                <button 
                                  onClick={() => handleEditContent(option.id, editableContent[option.id] || option.content)}
                                  className="absolute bottom-3 right-3 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                  title="Edit content"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Feedback Section */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Give Feedback</label>
                      <div className="relative">
                        <textarea 
                          value={feedback}
                          onChange={(e) => setFeedback(e.target.value)}
                          className="w-full px-3 py-2 pr-24 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          rows={4}
                          placeholder="Enter your feedback here..."
                        />
                        
                        {/* Regenerate Button inside Feedback Box */}
                        <div className="absolute bottom-2 right-2">
                          <button 
                            onClick={handleRegenerateContent}
                            disabled={!feedback.trim() || isRegenerating}
                            className="flex items-center space-x-2 px-3 py-1 text-xs rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                              borderRadius: '12px',
                              border: '1px solid #E2D5FF',
                              background: '#FFF',
                              padding: '8px',
                              marginBottom: '10px',
                              color: '#4F3E98',
                            }}
                          >
                            {isRegenerating ? (
                              <>
                                <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span className="text-xs font-medium">Regenerating...</span>
                              </>
                            ) : (
                              <>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="none">
                                  <path d="M0 9.65445C5.187 9.65445 6.55186 16.2064 6.55186 16.2064C6.55186 16.2064 7.91671 9.65445 13.1037 9.65445C7.91671 9.65445 6.55186 3.10254 6.55186 3.10254C6.55186 3.10254 5.187 9.65445 0 9.65445Z" fill="#4F3E98"/>
                                  <path d="M10.3457 16.2063C13.5561 16.9005 13.794 19.9995 13.794 19.9995C13.794 19.9995 14.032 16.9005 17.2424 16.2063C14.032 15.5121 13.794 12.4131 13.794 12.4131C13.794 12.4131 13.5561 15.5121 10.3457 16.2063Z" fill="#4F3E98"/>
                                  <path d="M11.0352 4.82773C12.1983 5.05193 13.2598 5.64072 14.0658 6.50875C14.8718 7.37677 15.3805 8.47892 15.518 9.65545C15.6556 8.47892 16.1642 7.37677 16.9702 6.50875C17.7762 5.64072 18.8377 5.05193 20.0009 4.82773C18.8377 4.60352 17.7762 4.01473 16.9702 3.14671C16.1642 2.27868 15.6556 1.17653 15.518 0C15.3805 1.17653 14.8718 2.27868 14.0658 3.14671C13.2598 4.01473 12.1983 4.60352 11.0352 4.82773Z" fill="#4F3E98"/>
                                </svg>
                                <span className="text-xs font-medium">Regenerate</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Approve Button */}
                    <div className="flex justify-end">
                      <button 
                        onClick={handleApproveModule}
                        disabled={isTransitioning || !selectedOptionId}
                        className="px-6 py-2 text-white rounded-lg font-medium transition-all duration-200 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          background: selectedOptionId 
                            ? 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)'
                            : '#9CA3AF'
                        }}
                      >
                        {isTransitioning ? 'Approving...' : selectedOptionId ? 'Approve Module' : 'Select an Option First'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Next Button - Show when all modules are completed */}
              {modules.filter(module => module.status === 'completed').length === modules.length && modules.length > 0 && (
                <div className="flex justify-center mt-auto pt-6">
                  <button
                    onClick={() => {
                      if (isTransitioning) return;
                      setIsTransitioning(true);
                      
                      // Mark current main step as completed and next as active
                      setMainSteps(prev => prev.map((step, index) => 
                        index === currentMainStep 
                          ? { ...step, status: 'completed' as const }
                          : index === currentMainStep + 1
                          ? { ...step, status: 'active' as const }
                          : step
                      ));
                      
                      // Move to next main step (Final Preview)
                      setCurrentMainStep(prev => prev + 1);
                      setCurrentModuleIndex(0);
                      
                      // Reset transition state
                      setTimeout(() => setIsTransitioning(false), 300);
                    }}
                    disabled={isTransitioning}
                    className="w-full px-8 py-3 text-white rounded-lg font-medium transition-all duration-200 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      borderRadius: '12px',
                      background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)'
                    }}
                  >
                    {isTransitioning ? 'Loading...' : 'Next'}
                  </button>
                </div>
              )}
              
              {/* Render Pending Modules */}
              {modules.filter(module => module.status === 'pending').map((module) => (
                <div key={module.id} className="mb-6">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <span className="text-lg font-medium text-gray-600">{module.name}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Preview Sidebar */}
            <div className="lg:col-span-3 bg-white rounded-lg shadow-sm border" style={{ 
              borderRadius: '10px', 
              border: '1px solid #ECECEC',
              background: '#F8F9FE',
              padding: '24px' 
            }}>
              <h3 className="text-lg font-semibold text-gray-900 mb-6">APPROVED MODULES</h3>
              
              {/* Preview Cards */}
              <div className="space-y-4">
                {modules.filter(module => module.status === 'completed').map((module) => (
                  <div key={module.id} className="bg-white border border-gray-200 rounded-lg p-4" style={{
                    borderRadius: '10px',
                    border: '1px solid #279808',
                    background: '#EFFFEB'
                  }}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-medium text-gray-900">{module.name}</div>
                      <div className="flex items-center space-x-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M21 11.9957L19.0036 9.60487L19.2818 6.44004L16.3282 5.73483L14.7818 3L12 4.25561L9.21818 3L7.67182 5.73483L4.71818 6.43144L4.99636 9.59627L3 11.9957L4.99636 14.3865L4.71818 17.56L7.67182 18.2652L9.21818 21L12 19.7358L14.7818 20.9914L16.3282 18.2566L19.2818 17.5514L19.0036 14.3865L21 11.9957ZM10.3636 16.2957L7.09091 12.8557L8.24455 11.6431L10.3636 13.8619L15.7555 8.19446L16.9091 9.41567L10.3636 16.2957Z" fill="#259403"/>
                        </svg>
                        <span className="text-xs font-medium text-green-700">Approved</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {module.content || 'Module content will appear here after approval.'}
                    </p>
                  </div>
                ))}
                
                {modules.filter(module => module.status === 'completed').length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-sm">No approved modules yet.</p>
                    <p className="text-xs text-gray-400 mt-1">Complete modules to see them here.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          
        </div>
      );
    } catch (error) {
      return (
        <div className="bg-white rounded-lg shadow-sm border" style={{ borderRadius: '20px', padding: '24px' }}>
          <h3 className="text-2xl font-semibold text-gray-900 mb-6">ERROR</h3>
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg">Error rendering Step 2</p>
            <p className="text-sm mt-2">Check console for details</p>
            <p className="text-xs mt-1">{error instanceof Error ? error.message : 'Unknown error'}</p>
          </div>
        </div>
      );
    }
  };

  const renderStep3Content = () => {
    return (
      <div className="bg-white rounded-lg shadow-sm">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">COPYWRITING</h3>
        
        {/* Preview Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-lg font-semibold text-gray-900">PREVIEW</h4>
            <button 
              onClick={handleDownloadCopy}
              className="px-4 py-2 border border-purple-300 text-gray-900 rounded-lg hover:bg-purple-50 transition-colors"
            >
              Download
            </button>
          </div>
          
          {/* Module List */}
          <div className="space-y-4">
            {[1, 2, 3, 4, 5, 6, 7].map((moduleNumber) => (
              <div key={moduleNumber}>
                <div className="bg-purple-50 px-3 py-2 rounded-t-lg">
                  <span className="text-sm font-medium text-gray-900">Module {moduleNumber}</span>
                </div>
                <div className="bg-white border border-gray-200 rounded-b-lg p-4">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Potential buyers may be concerned about the price point relative to similar products in the market. They might question if the premium price is justified by the quality and experience it offers.
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderMainContent = () => {
    
    try {
      switch (currentMainStep) {
        case 0:
          return renderStep1Content();
        case 1:
          // Copywriting main step UI
          return renderStep2Content();
        case 2:
          return renderStep3Content();
        default:
          return (
            <div className="bg-white rounded-lg shadow-sm border" style={{ borderRadius: '20px', padding: '24px' }}>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Unknown Step</h3>
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg">Step {currentMainStep} is not implemented yet.</p>
              </div>
            </div>
          );
      }
    } catch (error) {
      return (
        <div className="bg-white rounded-lg shadow-sm border" style={{ borderRadius: '20px', padding: '24px' }}>
          <h3 className="text-2xl font-semibold text-gray-900 mb-6">Error</h3>
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg">An error occurred while rendering the content.</p>
            <p className="text-sm mt-2">Please check the console for details.</p>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #D7BEFF 0%, #FDFDFF 100%)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Product Information Card */}
        {projectData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 mb-8"
            style={{ 
              marginTop: '64px',
              borderRadius: '20px',
              border: '1px solid #4A3B8E',
              background: '#F4F3FF'
            }}
          >
            <div className="flex items-stretch space-x-6">
              <div className="flex-shrink-0">
                <div className="bg-white rounded-lg flex items-center justify-center border-2 border-pink-400 shadow-sm relative overflow-hidden" style={{ width: '160px', height: '180px', flexShrink: 0 }}>
                  <img 
                    src={getProjectImage(backendProjectData?.project || projectData)} 
                    alt={backendProjectData?.project?.name || projectData.name}
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              
              <div className="flex-1">
                <div className="flex items-center space-x-4 mb-4">
                                      <h2 className="text-2xl font-bold text-gray-900">{backendProjectData?.project?.name || projectData.name}</h2>
                    <div className="bg-gray-100 text-gray-700 px-3 py-1 rounded-lg text-sm font-medium">
                      ASIN: {backendProjectData?.project?.amazon_link?.split('/dp/')[1]?.split('?')[0] || (projectData as any)?.amazon_link?.split('/dp/')[1]?.split('?')[0] || 'N/A'}
                    </div>
                  <div className="bg-white border border-gray-200 text-gray-800 px-3 py-1 rounded-lg text-sm font-medium flex items-center space-x-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M19 4H17V3C17 2.73478 16.8946 2.48043 16.7071 2.29289C16.5196 2.10536 16.2652 2 16 2C15.7348 2 15.4804 2.10536 15.2929 2.29289C15.1054 2.48043 15 2.73478 15 3V4H9V3C9 2.73478 8.89464 2.48043 8.70711 2.29289C8.51957 2.10536 8.26522 2 8 2C7.73478 2 7.48043 2.10536 7.29289 2.29289C7.10536 2.48043 7 2.73478 7 3V4H5C4.20435 4 3.44129 4.31607 2.87868 4.87868C2.31607 5.44129 2 6.20435 2 7V19C2 19.7956 2.31607 20.5587 2.87868 21.1213C3.44129 21.6839 4.20435 22 5 22H19C19.7956 22 20.5587 21.6839 21.1213 21.1213C21.6839 20.5587 22 19.7956 22 19V7C22 6.20435 21.6839 5.44129 21.1213 4.87868C20.5587 4.31607 19.7956 4 19 4ZM20 19C20 19.2652 19.8946 19.5196 19.7071 19.7071C19.5196 19.8946 19.2652 20 19 20H5C4.73478 20 4.48043 19.8946 4.29289 19.7071C4.10536 19.5196 4 19.2652 4 19V12H20V19ZM20 10H4V7C4 6.73478 4.10536 6.48043 4.29289 6.29289C4.48043 6.10536 4.73478 6 5 6H7V7C7 7.26522 7.10536 7.51957 7.29289 7.70711C7.48043 7.89464 7.73478 8 8 8C8.26522 8 8.51957 7.89464 8.70711 7.70711C8.89464 7.51957 9 7.26522 9 7V6H15V7C15 7.26522 15.1054 7.51957 15.2929 7.70711C15.4804 7.89464 15.7348 8 16 8C16.2652 8 16.5196 7.89464 16.7071 7.70711C16.8946 7.51957 17 7.26522 17 7V6H19C19.2652 6 19.5196 6.10536 19.7071 6.29289C19.8946 6.48043 20 6.73478 20 7V10Z" fill="#4A3B8E"/>
                    </svg>
                    <span>{dashboardData?.project?.created_at ? new Date(dashboardData.project.created_at).toLocaleDateString('en-GB') : 'N/A'}</span>
                  </div>
                </div>
                
                <p className="text-gray-600 mb-6 max-w-2xl leading-relaxed">
                  Your project officially kicks off once: This form is submitted, and We get access to your assets folder. This won't take more than 10 minutes.
                </p>

                {/* Action Buttons */}
                <div className="flex items-center space-x-4">
                  <button
                    onClick={handleBackToResearch}
                    className="flex items-center space-x-2 px-4 py-2 border border-purple-600 text-purple-700 rounded-lg hover:bg-purple-50 transition-all duration-200"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    <span className="text-sm font-medium">Back to Research</span>
                  </button>
                  
                  <button
                    onClick={handleDownloadCopy}
                    className="flex items-center space-x-2 px-4 py-2 border border-purple-600 text-purple-700 rounded-lg hover:bg-purple-50 transition-all duration-200"
                  >
                    <Download className="h-4 w-4" />
                    <span className="text-sm font-medium">Download Research</span>
                  </button>
                  
                                      <button
                      onClick={handleStartCopyAgent}
                      disabled={isStartingSession}
                      className="flex items-center space-x-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        background: '#4A3B8E'
                      }}
                    >
                      {isStartingSession ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span className="text-sm font-medium">Starting Copy Agent...</span>
                        </>
                      ) : (
                        <span className="text-sm font-medium">Generate Copy</span>
                      )}
                    </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Main Steps Progress */}
        <div className="bg-white rounded-lg shadow-sm border mb-8" style={{ borderRadius: '20px', padding: '24px' }}>
          <div className="flex items-center justify-between mb-8">
            {mainSteps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div 
                  className="flex flex-col items-center space-y-2 cursor-pointer"
                  onClick={() => {
                    setCurrentMainStep(index);
                    setCurrentSubStepIndex(0);
                    setChatMessages([]);
                  }}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    step.status === 'completed' 
                      ? 'bg-green-500' 
                      : step.status === 'active'
                      ? 'bg-purple-500'
                      : 'bg-gray-300'
                  }`} style={{
                    backgroundColor: step.status === 'active' ? '#4A3B8E' : undefined
                  }}>
                    {step.status === 'completed' ? (
                  <Check className="h-5 w-5 text-white" />
                    ) : (
                      <span className="text-sm font-medium text-white">{index + 1}</span>
                    )}
                </div>
                  <div className="text-center">
                    <div className="text-sm font-medium text-gray-900">{step.name}</div>
                    {step.status === 'active' && (
                <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full mt-1" style={{
                  backgroundColor: 'rgba(74, 59, 142, 0.1)',
                  color: '#4A3B8E'
                }}>In Progress</span>
                    )}
                    {step.status === 'completed' && (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full mt-1">Completed</span>
                    )}
              </div>
                </div>
                
                {/* Progress line between steps */}
                {index < mainSteps.length - 1 && (
                  <div className={`w-96 h-1 mx-4 ${
                    step.status === 'completed' ? 'bg-green-500' : 'bg-gray-300'
                  }`} style={{
                    backgroundColor: step.status === 'completed' ? '#4A3B8E' : undefined,
                    borderRadius: '4px'
                  }}></div>
                )}
              </div>
            ))}
          </div>
          
          {/* Grey border after stepper */}
          <div className="border-t border-gray-200 mb-8"></div>
          
          {/* Main Content */}
          {renderMainContent()}
        </div>
      </div>

      {/* Preview Popup */}
      {showPreviewPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{previewTitle}</h3>
              <button 
                onClick={() => setShowPreviewPopup(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
              {previewContent}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CopyAgentPage; 